import React, { FunctionComponent, useEffect, useState } from 'react';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import { LoadingIndicator } from '@havenengineering/module-shared-library/dist/components/LoadingIndicator/LoadingIndicator';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { SectionHeader } from '@havenengineering/module-shared-owners-ui/dist/components/SectionHeader/SectionHeader';
import useSWR from 'swr';

import {
  displayPrice,
  getAvailableForByOptionType,
} from '../../../helpers/draindown';
import {
  fetchWrapper,
  swrFetcher,
  withApiBaseUrl,
} from '../../../helpers/fetch';
import { Package } from '../Packages';
import ServiceCard from '../ServiceCard';
import styles from './AdditionalServices.module.scss';
import { options as drainDownOptions } from './OptionSelect';

type Props = Pick<
  DrainDownForm,
  'option' | 'drainDownAdditionalExtras' | 'moistureTrap'
> & {
  onStepChange: () => void;
  handleAdditionalExtraChange: (
    drainDownAdditionalExtras: string[],
    moistureTrap: number
  ) => void;
  parkCode: string;
  handleSkip: () => void;
};

const AdditionalServices: FunctionComponent<Props> = ({
  onStepChange,
  handleAdditionalExtraChange,
  option,
  drainDownAdditionalExtras,
  moistureTrap,
  parkCode,
  handleSkip,
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [availableAdditionalServices, setAvailableAdditionalServices] =
    useState<DrainDownAdditionalService[]>([]);
  const [selectedExtras, setSelectedExtras] = useState<
    DrainDownSelectedAdditionalService[]
  >([]);
  const [total, setTotal] = useState<number>(0);

  const { data: drainDownPackages } = useSWR<Package[], Error>(
    `/draindown/packages/${parkCode}?availableFor=${getAvailableForByOptionType(
      option
    )}`,
    swrFetcher
  );

  const selectedDrainDownOption = drainDownOptions.find(
    (o) => o.type === option
  );
  const selectedPackageOption = drainDownPackages?.find(
    (p) => p.tag === option
  );

  const getInitialExtras = (
    availableAdditionalServices: DrainDownAdditionalService[]
  ) => {
    const extras = drainDownAdditionalExtras.map((tag) => ({
      service: availableAdditionalServices.find(
        (s) => s.tag === tag
      ) as DrainDownAdditionalService,
      quantity: 1,
    }));
    if (moistureTrap > 0) {
      return [
        ...extras,
        {
          service: availableAdditionalServices.find(
            (s) => s.tag === 'moistureTrap'
          ) as DrainDownAdditionalService,
          quantity: moistureTrap,
        },
      ];
    }
    return extras;
  };

  useEffect(() => {
    const fetchAdditionalServices = async () => {
      setLoading(true);
      const availableFor = getAvailableForByOptionType(option);
      try {
        const services = await fetchWrapper(
          withApiBaseUrl(
            `/draindown/additional-services/${parkCode}?availableFor=${availableFor}`
          ),
          {
            method: 'GET',
            credentials: 'include',
          }
        );
        if (services.length === 0) return handleSkip();

        setAvailableAdditionalServices(services);
        setSelectedExtras(getInitialExtras(services));
        setLoading(false);
        setError(false);
      } catch (error) {
        setLoading(false);
        setError(true);
      }
    };
    fetchAdditionalServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parkCode, option]);

  const handleChange = (change: DrainDownSelectedAdditionalService) => {
    setSelectedExtras((prev) => {
      const newExtras = prev.filter(
        (p) => p.service.tag !== change.service.tag
      );
      return change.quantity === 0 ? newExtras : [...newExtras, change];
    });
  };

  useEffect(() => {
    const newTotal = selectedExtras.reduce(
      (total, extra) => {
        return total + extra.service.price * extra.quantity;
      },
      selectedPackageOption ? 0 : selectedDrainDownOption?.price || 0
    );

    if (selectedPackageOption) {
      const totalWithPackageItems =
        selectedPackageOption?.additionalServices?.reduce((total, item) => {
          const additionalServicePrice =
            item.additionalService.price * item.quantity;

          return total + additionalServicePrice;
        }, parseFloat(selectedPackageOption.basePackagePrice));

      setTotal(
        newTotal +
          (totalWithPackageItems -
            totalWithPackageItems * parseFloat(selectedPackageOption.discount))
      );
    } else {
      setTotal(newTotal);
    }
  }, [selectedExtras, selectedDrainDownOption, selectedPackageOption]);

  const handleNext = () => {
    const moistureTrapValue =
      selectedExtras.find((se) => se.service.tag === 'moistureTrap')
        ?.quantity || 0;
    const drainDownAdditionalExtrasValue = selectedExtras
      .filter((se) => se.service.tag !== 'moistureTrap')
      .map((se) => se.service.tag);
    handleAdditionalExtraChange(
      drainDownAdditionalExtrasValue,
      moistureTrapValue
    );
    onStepChange();
  };

  return (
    <>
      <div className={styles.root}>
        <SectionHeader title="Additional Services" />
        <div className={styles.contentContainer}>
          {loading && (
            <div className={styles.servicesList}>
              <LoadingIndicator loading />
            </div>
          )}
          {!loading && error && (
            <div className={styles.servicesList}>
              <InlineMessaging
                type={InlineMessagingTheme.ERROR_THEME}
                title="Error"
                message="Something went wrong, please try again"
                dismissible={false}
              />
            </div>
          )}
          {!loading && !error && availableAdditionalServices && (
            <div className={styles.servicesList}>
              {availableAdditionalServices
                .filter((s) => s.description)
                .sort((a, b) => b.price - a.price)
                .map((s) => (
                  <ServiceCard
                    key={s.tag}
                    services={[s]}
                    name={s.name}
                    description={s.description}
                    selectedExtras={selectedExtras}
                    handleChange={handleChange}
                  />
                ))}
              {availableAdditionalServices.filter((s) => !s.description)
                .length > 0 && (
                <ServiceCard
                  services={availableAdditionalServices.filter(
                    (s) => !s.description
                  )}
                  name="Other extras"
                  selectedExtras={selectedExtras}
                  handleChange={handleChange}
                />
              )}
            </div>
          )}
          <div className={styles.summaryContainer}>
            <div className={styles.summaryCard}>
              <h1 className={styles.title}>Summary</h1>
              <hr />
              {selectedDrainDownOption && (
                <>
                  <h2>Selected option</h2>
                  {selectedDrainDownOption.includes?.map((item) => (
                    <div key={item} className={styles.priceRow}>
                      <p>{item}</p>
                    </div>
                  ))}
                  <div className={styles.priceRow}>
                    <p>cost</p>
                    <p>{displayPrice(selectedDrainDownOption?.price || 0)}</p>
                  </div>
                </>
              )}
              {selectedPackageOption && (
                <>
                  <h2>Selected package</h2>
                  <div className={styles.priceRow}>
                    <p>{selectedPackageOption.basePackageName}</p>
                    <p>
                      {displayPrice(
                        parseFloat(selectedPackageOption.basePackagePrice)
                      )}
                    </p>
                  </div>
                  {selectedPackageOption.additionalServices?.map((item) => (
                    <div
                      key={item.additionalService.tag}
                      className={styles.priceRow}
                    >
                      <p>
                        {item.quantity ? `x ${item.quantity}` : ''}{' '}
                        {item.additionalService.name}{' '}
                        {item.quantity
                          ? `(${displayPrice(
                              item.additionalService.price
                            )} each)`
                          : ''}{' '}
                      </p>
                      <p>
                        {displayPrice(
                          item.additionalService.price * item.quantity
                        )}
                      </p>
                    </div>
                  ))}
                </>
              )}
              {selectedPackageOption && (
                <div className={styles.priceRow}>
                  <h2>Package discount</h2>
                  <p>{parseFloat(selectedPackageOption.discount) * 100}% off</p>
                </div>
              )}
              {selectedExtras.length > 0 && (
                <>
                  <h2>Extras</h2>
                  {selectedExtras.map((se) => (
                    <div
                      key={`summary-extra-${se.service.tag}`}
                      className={styles.priceRow}
                    >
                      {se.service.type === 'simple' ? (
                        <p>{se.service.name}</p>
                      ) : (
                        <p>
                          {se.quantity} x {se.service.name} (
                          {displayPrice(se.service.price)} each)
                        </p>
                      )}
                      <p>{displayPrice(se.service.price * se.quantity)}</p>
                    </div>
                  ))}
                </>
              )}
              <hr />
              <div className={styles.priceRow}>
                <h2>Total</h2>
                <p className={styles.totalPrice}>{displayPrice(total)}</p>
              </div>
            </div>
            <Button
              size="medium"
              fullWidth
              className={styles.desktopNextButton}
              onClick={handleNext}
              disabled={loading}
              data-testid="next-btn"
            >
              Next
            </Button>
          </div>
        </div>
      </div>
      <div className={styles.actionBar}>
        <h2>
          Total <span className={styles.totalPrice}>{displayPrice(total)}</span>
        </h2>
        <Button
          data-testid="next-mobile-btn"
          size="large"
          onClick={handleNext}
          disabled={loading}
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default AdditionalServices;
